"use client"

import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID } from '../config/environment';
import LoadingSpinner from "./LoadingSpinner";
import { useTheme } from "../contexts/ThemeContext"

function Login() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const { login, loginWithGoogle } = useAuth()
  const navigate = useNavigate()
  const { theme } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      await login(email, password)
      navigate("/dashboard")
    } catch (error) {
      setError(error.response?.data?.detail || "Login failed")
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true)
    setError("")
    try {
      await loginWithGoogle(credentialResponse.credential)
      navigate('/dashboard')
    } catch (err) {
      setError('Google sign-in failed')
    } finally {
      setLoading(false)
    }
  };

  return (
    <div className={theme === "dark" ? "min-h-screen flex items-center justify-center bg-black text-white py-12 px-4 sm:px-6 lg:px-8" : "min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 py-12 px-4 sm:px-6 lg:px-8"}>
      {loading && (
        <div className={theme === "dark" ? "fixed inset-0 z-50 flex items-center justify-center bg-black/80" : "fixed inset-0 z-50 flex items-center justify-center bg-black/30"}>
          <LoadingSpinner />
        </div>
      )}
      <div className={theme === "dark" ? "max-w-md w-full space-y-8 bg-black border border-gray-800 rounded-2xl shadow-2xl p-8" : "max-w-md w-full space-y-8 bg-white/80 backdrop-blur-md rounded-2xl shadow-2xl p-8"}>
        <div className="flex flex-col items-center">
          <img src="/brand-logo.svg" alt="Logo" className={theme === "dark" ? "h-16 w-16 mb-2 rounded-full shadow-lg border-2 border-gray-700 bg-black" : "h-16 w-16 mb-2 rounded-full shadow-lg border-2 border-indigo-400"} />
          <h2 className={theme === "dark" ? "mt-2 text-center text-3xl font-extrabold text-white drop-shadow" : "mt-2 text-center text-3xl font-extrabold text-gray-900 drop-shadow"}>Sign in to your account</h2>
          <p className={theme === "dark" ? "mt-2 text-center text-sm text-gray-200" : "mt-2 text-center text-sm text-gray-600"}>Access Inflatein AI Studio</p>
        </div>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <div className="flex flex-col items-center mb-4">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => setError('Google sign-in failed')}
              width="100%"
            />
          </div>
        </GoogleOAuthProvider>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && <div className={theme === "dark" ? "bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded shadow" : "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow"}>{error}</div>}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                type="email"
                required
                className={theme === "dark" ? "appearance-none rounded-t-xl relative block w-full px-4 py-3 border border-gray-700 placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 focus:z-10 sm:text-base bg-black" : "appearance-none rounded-t-xl relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 focus:z-10 sm:text-base bg-white/90"}
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
            <div>
              <input
                type="password"
                required
                className={theme === "dark" ? "appearance-none rounded-b-xl relative block w-full px-4 py-3 border border-gray-700 placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 focus:z-10 sm:text-base bg-black" : "appearance-none rounded-b-xl relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 focus:z-10 sm:text-base bg-white/90"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
          </div>
          <div className="text-right mb-2">
            <Link to="/forgot-password" className={theme === "dark" ? "text-sm text-white hover:text-indigo-400 font-medium transition-colors duration-200" : "text-sm text-indigo-600 hover:text-pink-500 font-medium transition-colors duration-200"}>
              Forgot Password?
            </Link>
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className={theme === "dark" ? "group relative w-full flex justify-center py-3 px-4 border border-gray-700 text-base font-semibold rounded-xl text-white bg-black hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400 disabled:opacity-60 shadow-lg transition-all duration-200" : "group relative w-full flex justify-center py-3 px-4 border border-transparent text-base font-semibold rounded-xl text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400 disabled:opacity-60 shadow-lg transition-all duration-200"}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                  Signing in...
                </span>
              ) : "Sign in"}
            </button>
          </div>
          <div className="text-center">
            <Link to="/register" className={theme === "dark" ? "text-white hover:text-indigo-400 font-medium transition-colors duration-200" : "text-indigo-600 hover:text-pink-500 font-medium transition-colors duration-200"}>
              {"Don't have an account? Sign up"}
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Login
